import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { EMPTY, from, switchMap, catchError, throwError } from 'rxjs';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

export const basicAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);
  const account =
    msalService.instance.getActiveAccount() ??
    msalService.instance.getAllAccounts()[0] ??
    null;

  if (!account) {
    // Frontend only calls authenticated endpoints — a request without token would guarantee a 401
    return throwError(() => new Error('No MSAL account available for authenticated request'));
  }

  // Use only the API scope for the access token — not openid/profile (those are for the ID token)
  const apiScope = environment.loginRequest.scopes.filter(s => s.includes('.default'));

  const withToken = (token: string) =>
    next(req.clone({ headers: req.headers.set('Authorization', 'Bearer ' + token) }));

  const acquire = (forceRefresh = false) =>
    from(msalService.instance.acquireTokenSilent({ scopes: apiScope, account, forceRefresh }));

  return acquire().pipe(
    switchMap((result) => withToken(result.accessToken)),
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          // Backend rejected the token: force a refresh and retry the request once
          return acquire(true).pipe(
            switchMap((result) => withToken(result.accessToken)),
          );
        }
        return throwError(() => error);
      }
      if (error instanceof InteractionRequiredAuthError) {
        // Abort the request — the redirect takes over and re-authenticates the user
        msalService.instance.acquireTokenRedirect({ scopes: apiScope, account });
        return EMPTY;
      }
      console.warn('[MSAL] Silent token acquisition failed', error);
      return throwError(() => error);
    }),
  );
};
